import React, { useState, useEffect, useReducer } from 'react';
import {
  Box,
  IconButton,
  ClickAwayListener,
  Divider,
  Fade,
  Popper,
  CircularProgress,
  Tooltip,
  Button
} from '@mui/material';
import FeaturedPlayListOutlinedIcon from '@mui/icons-material/FeaturedPlayListOutlined';
import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import PaidIcon from '@mui/icons-material/Paid';
import { classes } from '../../styles';
import { IUserInfo } from '../../Header';
import { handleAPIRequest } from '../../../../Components/Utilities/handleAPIRequest';
import { formatUserPermissions } from '../../../../Components/Utilities/formatUserPermissions';

import { clearAllPreferenceCache } from '../../../../helpers/uiPreferencesHelpers';
import { debugMode, railsVersion } from '../../../../utils/debugInfo';
import TokenUsageModal from './TokenUsageModal';
import {
  GenericTableState,
  GenericTableAction,
  initialTableState
} from '../../../../Components/GenericTable/genericTableReducer';

const ProfileDropdownPopper = ({
  menuOpen,
  setMenuOpen,
  setChangingEntity,
  avatarAnchorEl,
  userInfo
}: {
  menuOpen: boolean;
  setMenuOpen: (menuOpen: boolean) => void;
  setChangingEntity: (changingEntity: boolean) => void;
  avatarAnchorEl: HTMLElement | null;
  userInfo: IUserInfo;
}) => {
  const [loadingLogout, setLoadingLogout] = useState(false);
  const [masqueradeIconHovered, setMasqueradeIconHovered] = useState(false);
  const [tokenCount, setTokenCount] = useState(0);
  const [maxTokens, setMaxTokens] = useState(0);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [tokenUsageTableState, tokenUsageDispatch] = useReducer(
    (state: GenericTableState, action: GenericTableAction): GenericTableState => {
      switch (action.type) {
        case 'SET_SEARCH_QUERY':
          return { ...state, searchQuery: action.payload };
        case 'SET_PAGE':
          return {
            ...state,
            searchSettings: { ...state.searchSettings, page: action.payload }
          };
        case 'SET_LIMIT':
          return {
            ...state,
            searchSettings: { ...state.searchSettings, limit: action.payload }
          };
        case 'SET_SORT':
          return {
            ...state,
            searchSettings: {
              ...state.searchSettings,
              sort: action.payload.sortBy,
              sort_order: action.payload.sortOrder
            }
          };
        default:
          return state;
      }
    },
    initialTableState(
      [
        { id: 'id', label: 'ID', sortable: true, canToggle: false, enabled: true },
        { id: 'kind', label: 'Kind', sortable: true, canToggle: false, enabled: true },
        { id: 'amount', label: 'Amount', sortable: true, canToggle: false, enabled: true },
        {
          id: 'candidate_name',
          label: 'Candidate Name',
          sortable: true,
          canToggle: false,
          enabled: true
        },
        { id: 'created_at', label: 'Created At', sortable: true, canToggle: false, enabled: true }
      ],
      { sortBy: 'created_at', sortOrder: 'desc' }
    )
  );
  const userPermissions = formatUserPermissions();

  const sourcePermission = userPermissions['Accounts - User']?.['Edit Source'] ? true : false;

  const profileMenuItems = [
    sourcePermission && {
      label: 'Sources',
      icon: <FeaturedPlayListOutlinedIcon />,
      path: userInfo.links.sourceURL
    },
    {
      label: 'Support Centre',
      icon: <LiveHelpOutlinedIcon />,
      path: 'https://scouttalent.my.site.com/myresourcecentre/s/'
    },
    {
      label: 'Settings',
      icon: <SettingsOutlinedIcon />,
      path: '/admin/settings'
    },
    {
      label: 'Log out',
      icon: !loadingLogout ? <LogoutOutlinedIcon /> : <CircularProgress size={24} />,
      path: '#'
    }
  ];

  const fetchTokenProfile = async () => {
    await handleAPIRequest({
      url: '/api/v4/token_profiles',
      method: 'GET',
      onSuccess: (data) => {
        setTokenCount(data.token_count);
        setMaxTokens(data.max_tokens);
      }
    });
  };

  const logOut = () => {
    handleAPIRequest({
      url: '/api/v4/logout',
      method: 'DELETE',
      setLoaderState: setLoadingLogout,
      onSuccess: (data) => {
        window.location.href = data?.redirect_url;
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('userPermissionsExpiry');
        clearAllPreferenceCache();
      }
    });
  };

  const unmasquerade = () => {
    window.location.href = userInfo?.links.unmasqueradeURL;
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('userPermissionsExpiry');
    clearAllPreferenceCache();
  };

  const openChangingEntity = () => {
    setChangingEntity(true);
    setMenuOpen(false);
  };

  const openTokenModal = () => {
    setTokenModalOpen(true);
    setMenuOpen(false);
  };

  const handleTokenModalClose = () => {
    setTokenModalOpen(false);
  };

  useEffect(() => {
    fetchTokenProfile();
  }, []);

  const showAvatar = userInfo?.links.unmasqueradeURL ? (
    <Tooltip title="Unmasquerade" placement="top">
      <Box
        sx={{
          ...classes.profileAvatar,
          ...classes.avatarSmall,
          ...classes.masqueradeAvatar
        }}
        onClick={unmasquerade}
        onMouseEnter={() => setMasqueradeIconHovered(true)}
        onMouseLeave={() => setMasqueradeIconHovered(false)}
      >
        {masqueradeIconHovered ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </Box>
    </Tooltip>
  ) : (
    <Box sx={{ ...classes.profileAvatar, ...classes.avatarSmall }}>
      {userInfo?.firstName[0] + userInfo?.lastName[0]}
    </Box>
  );

  return (
    <>
      <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
        <Popper
          open={menuOpen}
          anchorEl={avatarAnchorEl}
          placement="bottom-end"
          transition
          sx={{ zIndex: 1400 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Box sx={classes.profileDropdownMenuContainer}>
                <Box sx={classes.profileInformation}>
                  {showAvatar}
                  <Box sx={classes.profileNameAndEntityContainer}>
                    <Box
                      sx={classes.profileName}
                    >{`${userInfo?.firstName} ${userInfo?.lastName}`}</Box>
                    {userInfo?.entity && (
                      <a href={userInfo?.entity.url}>
                        <Box>{userInfo?.entity.name}</Box>
                      </a>
                    )}
                    <Box sx={classes.roleInformationWrapper}>
                      {userInfo.roleNames && (
                        <Tooltip title={userInfo.roleNames} placement="bottom">
                          <Box sx={classes.roleInformation}>{userInfo.roleNames}</Box>
                        </Tooltip>
                      )}
                      {userInfo?.entity?.multiEntitiesAccess && (
                        <Tooltip title="Switch entity" placement="bottom">
                          <IconButton onClick={openChangingEntity}>
                            <SwitchAccountIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {debugMode && (
                      <Box sx={{ display: 'block' }}> Rails Version: {railsVersion} </Box>
                    )}
                    {maxTokens > 0 && (
                      <Button
                        onClick={openTokenModal}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          textTransform: 'none',
                          padding: '4px 8px',
                          color: 'inherit',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <PaidIcon />
                        <Box>
                          {tokenCount} / {maxTokens}
                        </Box>
                      </Button>
                    )}
                  </Box>
                </Box>
                <Divider variant="fullWidth" />
                <Box sx={classes.profileDropdownMenuItemsContainer}>
                  {profileMenuItems
                    .filter((item) => item)
                    .map((item, index) => {
                      if (!item || typeof item === 'boolean') return null;
                      return (
                        <a
                          href={item.path}
                          key={index}
                          onClick={item.path === '#' ? logOut : undefined}
                        >
                          <Box sx={classes.profileDropdownMenuItem}>
                            {item.icon}
                            {item.label}
                          </Box>
                        </a>
                      );
                    })}
                </Box>
              </Box>
            </Fade>
          )}
        </Popper>
      </ClickAwayListener>
      <TokenUsageModal
        isOpen={tokenModalOpen}
        handleClose={handleTokenModalClose}
        tableState={tokenUsageTableState}
        dispatch={tokenUsageDispatch}
      />
    </>
  );
};

export default ProfileDropdownPopper;
